import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { logService } from '../../services/logService';
import { selectSubject, removeSubject, clearSelectedSubjects } from '../../store/slices/logSlice';
import type { Subject } from '../../types/log';

interface SubjectListProps {
  onNewSubject: () => void;
  onEditSubject: (subject: Subject) => void;
}

const SubjectList: React.FC<SubjectListProps> = ({ onNewSubject, onEditSubject }) => {
  const dispatch = useDispatch();
  const { subjects, selectedSubjectIds, logs } = useSelector(
    (state: RootState) => state.logManager
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState<string | null>(null);

  const handleSelectSubject = (subjectId: string) => {
    dispatch(selectSubject(subjectId));
  };

  const handleShowAll = () => {
    dispatch(clearSelectedSubjects());
  };

  const handleDeleteClick = (subjectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSubjectToDelete(subjectId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!subjectToDelete) return;
    const success = await logService.deleteSubject(subjectToDelete);
    if (success) {
      dispatch(removeSubject(subjectToDelete));
    }
    setShowDeleteConfirm(false);
    setSubjectToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setSubjectToDelete(null);
  };

  const getLogCount = (subjectId: string) => {
    return logs.filter((l) => {
      const link = l.subjects?.some((s) => s.id === subjectId);
      return link && l.status !== 'completed';
    }).length;
  };

  const getSubjectColor = (subject: Subject) => {
    return subject.color || '#6b7280';
  };

  const isSubjectSelected = (subjectId: string) => selectedSubjectIds.includes(subjectId);

  return (
    <div className="p-4 space-y-2">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold dark:text-claude-darkTextSecondary text-claude-textSecondary uppercase tracking-wider">
          Subjects
        </h3>
        <button
          type="button"
          onClick={onNewSubject}
          className="p-1 rounded hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
          title="Add Subject"
        >
          <svg
            className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* All Logs */}
      <button
        type="button"
        onClick={handleShowAll}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left ${
          selectedSubjectIds.length === 0
            ? 'dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover dark:text-claude-darkText text-claude-text'
            : 'dark:text-claude-darkTextSecondary text-claude-textSecondary hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
        }`}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
        <span className="flex-1">All Logs</span>
        {selectedSubjectIds.length > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-claude-accent/10 text-claude-accent">
            Filtered
          </span>
        )}
        <span className="text-xs px-2 py-0.5 rounded-full bg-claude-accent/10 text-claude-accent">
          {logs.filter((l) => l.status !== 'completed').length}
        </span>
      </button>

      {/* Subjects List */}
      <div className="space-y-1">
        {subjects.map((subject) => {
          const logCount = getLogCount(subject.id);
          const isSelected = isSubjectSelected(subject.id);

          return (
            <div
              key={subject.id}
              className={`group flex items-center gap-3 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                isSelected
                  ? 'dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover'
                  : 'hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
              }`}
              onClick={() => handleSelectSubject(subject.id)}
            >
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: getSubjectColor(subject) }}
              />
              <span className="flex-1 truncate dark:text-claude-darkText text-claude-text">
                {subject.name}
              </span>
              <span className="text-xs text-claude-textSecondary capitalize">
                {subject.type}
              </span>
              {isSelected && (
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getSubjectColor(subject) }} />
              )}
              {logCount > 0 && !isSelected && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-claude-accent/10 text-claude-accent">
                  {logCount}
                </span>
              )}
              <div className="hidden group-hover:flex items-center gap-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditSubject(subject);
                  }}
                  className="p-1 rounded hover:dark:bg-claude-darkSurfaceMuted hover:bg-claude-surfaceMuted transition-colors"
                  title="Edit"
                >
                  <svg
                    className="h-3.5 w-3.5 dark:text-claude-darkTextSecondary text-claude-textSecondary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={(e) => handleDeleteClick(subject.id, e)}
                  className="p-1 rounded hover:bg-red-500/10 hover:text-red-500 transition-colors"
                  title="Delete"
                >
                  <svg
                    className="h-3.5 w-3.5 dark:text-claude-darkTextSecondary text-claude-textSecondary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {subjects.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary mb-3">
            No subjects yet
          </p>
          <button
            type="button"
            onClick={onNewSubject}
            className="text-sm text-claude-accent hover:underline"
          >
            Create your first subject
          </button>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleDeleteCancel} />
          <div className="relative w-full max-w-sm mx-4 bg-white dark:bg-claude-darkSurface rounded-xl shadow-xl p-6">
            <h3 className="text-lg font-semibold dark:text-claude-darkText text-claude-text mb-2">
              Delete Subject
            </h3>
            <p className="dark:text-claude-darkTextSecondary text-claude-textSecondary mb-6">
              Are you sure you want to delete this subject? Logs will be unlinked but not deleted.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-sm font-medium rounded-lg border dark:border-claude-darkBorder border-claude-border hover:dark:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectList;